/**
 * Light/dark theme (OutlierOS: header toggle, set before first paint, default light). ViralWise also follows
 * prefers-color-scheme on a first visit. Stored per browser.
 */
import { useState } from 'react';

export type Theme = 'light' | 'dark';
export const THEME_KEY = 'vw_theme';

/** Inline <script> for index.html: applies the theme before the first paint (no flash). */
export const THEME_BOOT_SCRIPT = `(function(){try{var t=localStorage.getItem('${THEME_KEY}');if(t!=='light'&&t!=='dark'){t=matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'}document.documentElement.classList.add(t)}catch(e){document.documentElement.classList.add('light')}})();`;

export function currentTheme(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function applyTheme(t: Theme) {
  const el = document.documentElement;
  el.classList.remove('light', 'dark');
  el.classList.add(t);
  try {
    localStorage.setItem(THEME_KEY, t);
  } catch {
    /* private mode */
  }
}

/** The theme follows the system until the user picks one; only an explicit toggle is saved. */
export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => (typeof document === 'undefined' ? 'light' : currentTheme()));
  return [theme, () => setTheme((t) => {
    const next = t === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    return next;
  })];
}
